/**
 * 発送一覧画面。Phase 7。
 *
 * シリアル番号入力 → status=completed にすると onDeliveryCompleted で sold_devices 一括登録。
 * UI: イベント管理画面と統一したカード形式リスト
 *
 * 2026-10-30: 件数は台数基準 (countDeliverySerials)、シリアルなし (deviceKind='none') の行は欄なし。
 * 保存時に空欄を詰めない (unitDetails = 保証期間等 と並びがずれるため)。
 */
import type { Delivery, DeliveryStatus } from '@goenhub/shared';
import { countDeliverySerials, padSerialNumbers, serialFieldLabel } from '@goenhub/shared';
import { CheckCircle, Pencil, Truck } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import { useDeliveries, useUpdateDelivery, type DeliveryRecord } from '@/hooks/useSalesOps';

const LABEL: Record<DeliveryStatus, string> = {
  pending: '未着手',
  in_transit: '配送中',
  completed: '納品完了',
  cancelled: 'キャンセル',
};

const STATUS_VARIANT: Record<DeliveryStatus, 'draft' | 'sent' | 'approved' | 'default'> = {
  pending: 'draft',
  in_transit: 'sent',
  completed: 'approved',
  cancelled: 'default',
};

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function DeliveryRow({
  delivery,
  dealTitle,
  onComplete,
  onSerialChange,
  isPending,
}: {
  delivery: DeliveryRecord;
  dealTitle: string;
  onComplete: () => void;
  onSerialChange: (lineItems: Delivery['lineItems']) => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  // 台数分の欄 (シリアルなしの行は空配列)
  const [drafts, setDrafts] = useState<string[][]>(
    delivery.lineItems.map((it) => padSerialNumbers(it)),
  );
  const draftLineItems = delivery.lineItems.map((it, i) => ({
    ...it,
    serialNumbers: drafts[i].map((s) => s.trim()),
  }));

  const handleSave = () => {
    // 空欄は詰めずに残す (unitDetails の並びと合わせるため)
    onSerialChange(draftLineItems);
    setEditing(false);
  };

  const { total: expectedSerials, filled: filledSerials } = countDeliverySerials(draftLineItems);

  return (
    <div className="border-b border-[var(--border)] last:border-b-0">
      <div className="flex items-center gap-4 p-4 hover:bg-[var(--bg-hover)] transition-colors">
        <Truck className="size-4 text-[var(--text-muted)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[13px] font-semibold truncate">
              {delivery.deliveryNumber}
            </span>
            <Badge variant={STATUS_VARIANT[delivery.status]}>{LABEL[delivery.status]}</Badge>
            <span className="text-xs text-[var(--text-muted)]">
              シリアル {filledSerials}/{expectedSerials}
            </span>
          </div>
          <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
            <span>{dealTitle}</span>
            <span>業者: {delivery.carrierName ?? '-'}</span>
            {delivery.trackingNumber && (
              <code className="font-mono bg-[var(--bg-hover)] px-1.5 py-0.5 rounded text-[10px]">
                追跡: {delivery.trackingNumber}
              </code>
            )}
            {delivery.shippedAt && <span>出荷: {fmtDate(delivery.shippedAt)}</span>}
            {delivery.deliveredAt && <span>納品: {fmtDate(delivery.deliveredAt)}</span>}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setEditing((p) => !p)}
          title={editing ? '閉じる' : 'シリアル編集'}
        >
          <Pencil />
        </Button>
        {delivery.status === 'in_transit' && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onComplete}
            title="納品完了"
            disabled={isPending || filledSerials < expectedSerials}
          >
            <CheckCircle />
          </Button>
        )}
      </div>
      {editing && (
        <div className="px-4 pb-4 pt-1 bg-[var(--bg-elevated)] border-t border-[var(--border)] space-y-3">
          {delivery.lineItems.map((it, i) => (
            <div key={i}>
              <p className="text-xs font-medium mb-1.5">
                {it.name}
                {it.variation && ` (${it.variation})`} × {it.quantity}
              </p>
              {it.deviceKind === 'none' ? (
                <p className="text-xs text-[var(--text-muted)]">シリアルなし（機器ではない商品）</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {drafts[i].map((value, j) => (
                    <input
                      key={j}
                      placeholder={`${serialFieldLabel(it.deviceKind)} ${j + 1}`}
                      value={value}
                      onChange={(e) => {
                        const next = drafts.map((a) => [...a]);
                        next[i][j] = e.target.value;
                        setDrafts(next);
                      }}
                      className="px-2 py-1 text-xs bg-white text-black border border-gray-300 rounded-sm w-40 font-mono"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          <Button type="button" variant="primary" size="sm" onClick={handleSave}>
            保存
          </Button>
        </div>
      )}
    </div>
  );
}

export function DeliveriesListPage() {
  const { data: deliveries, isLoading } = useDeliveries();
  const { data: deals } = useDeals();
  const update = useUpdateDelivery();

  const dealMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of deals ?? []) m.set(d.id, d.title);
    return m;
  }, [deals]);

  return (
    <Screen
      title="発送・納品管理"
      description="シリアル番号を入力して納品完了にすると、稼働機器が自動登録されます。"
    >
      {isLoading ? (
        <Spinner />
      ) : !deliveries || deliveries.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="発送はまだありません"
          description="入荷・検品が終わった発注を「顧客へ発送」すると、ここに作られます。"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {deliveries.map((dlv) => (
            <DeliveryRow
              key={dlv.id}
              delivery={dlv}
              dealTitle={dealMap.get(dlv.dealId) ?? dlv.dealId}
              isPending={update.isPending}
              onComplete={async () => {
                try {
                  await update.mutateAsync({
                    dealId: dlv.dealId,
                    deliveryId: dlv.id,
                    status: 'completed',
                  });
                  toast.success('納品完了: 稼働機器を自動登録しました');
                } catch (err) {
                  toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
                }
              }}
              onSerialChange={async (lineItems) => {
                try {
                  await update.mutateAsync({
                    dealId: dlv.dealId,
                    deliveryId: dlv.id,
                    lineItems,
                  });
                  toast.success('シリアル番号を保存しました');
                } catch (err) {
                  toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
                }
              }}
            />
          ))}
        </div>
      )}
    </Screen>
  );
}
